import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";

import { formatGameTime } from "@/features/analysis";
import { formatPercent } from "@/features/matches";

import { type DrillAsset, useDrillAssets } from "../../api/useDrillAssets";
import type { DrillSpec } from "../../model/Drill";
import { formatNumber } from "../../model/formatNumber";
import { AssetImage } from "./AssetImage";
import { useOptionView } from "./useOptionView";

const KILLED_BY = msg({
  message: "{killer} killed you {count, plural, one {# time} other {# times}} out of {total}",
});
const HEALED = msg({ message: "{healer} and the enemy team healed {amount}" });
const ESCAPED = msg({
  message: "{enemy} got away from you on low health {count, plural, one {# time} other {# times}}",
});
const YOU_PLAY = msg({ message: "You play {hero}" });
const WITH_HERO = msg({ message: "You play {hero} · {text}" });
const ESCAPED_FIRST = msg({ message: "first at {time}" });
const DAMAGE_SHARE = msg({
  message:
    "of the hero damage you took was {kind, select, bullet {bullet damage} spirit {spirit damage} other {melee}}",
});
const NO_DEFENSE = msg({ message: "no defense against it by {time}" });
const SOURCE_SHARE = msg({ message: "{name} {share}" });

type DrillSituationProps = { spec: DrillSpec };

// Карточка «Ситуация» над вопросом: момент матча, души, предметы, враги и герои — всегда
// портретом и именем, свой герой тоже. Задаче без момента карточка не нужна.
export const DrillSituation = ({ spec }: DrillSituationProps) => {
  const { i18n, t } = useLingui();
  const assets = useDrillAssets();
  const view = useOptionView();
  const time = formatGameTime(spec.moment_s ?? 0);
  const own = assets.hero(spec.hero_id);
  const target = spec.target_hero_id == null ? null : assets.hero(spec.target_hero_id);
  const withHero = (text: string) => i18n._({ ...WITH_HERO, values: { hero: own.name, text } });
  const chips = (label: string, ids: number[], lookup: (id: number) => DrillAsset, empty = "") => (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="w-28 shrink-0 text-xs text-subtle">{label}</span>
      <ul className="flex flex-wrap gap-1.5">
        {ids.map((id) => {
          const asset = lookup(id);
          return (
            <li
              key={id}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface-raised py-0.5 pr-2 pl-0.5 text-xs"
            >
              <AssetImage asset={asset} size={26} className="rounded-md" />
              {asset.name}
            </li>
          );
        })}
        {ids.length === 0 && empty !== "" && <li className="text-sm text-muted">{empty}</li>}
      </ul>
    </div>
  );
  const enemies = (label: string) => chips(label, spec.enemy_hero_ids, assets.hero);
  const withTarget = (text: string, note: string | null) => (
    <div className="flex items-center gap-4">
      {target !== null && (
        <AssetImage asset={target} size={56} className="rounded-xl border-2 border-bad" />
      )}
      <div className="flex flex-col gap-0.5">
        <span className="text-lg font-semibold">{text}</span>
        {note !== null && <span className="text-sm text-muted">{note}</span>}
      </div>
    </div>
  );
  let body: ReactNode = null;
  switch (spec.kind) {
    case "buy_now":
      body = (
        <>
          <div className="flex flex-wrap items-center gap-x-7 gap-y-3">
            <span className="flex items-center gap-2.5">
              <AssetImage asset={own} size={48} className="rounded-[10px] border-2 border-accent" />
              <span className="text-lg font-semibold">{own.name}</span>
            </span>
            <span className="flex flex-col">
              <span className="text-xs text-subtle">{t`Time`}</span>
              <span className="font-mono text-2xl font-semibold">{time}</span>
            </span>
            <span className="flex flex-col">
              <span className="text-xs text-subtle">{t`Souls in pocket`}</span>
              <span className="font-mono text-2xl font-semibold text-accent">
                {formatNumber(spec.souls ?? 0, i18n.locale)}
              </span>
            </span>
          </div>
          {chips(t`Your items`, spec.owned_item_ids, assets.item, t`Nothing yet`)}
          {enemies(t`Enemies`)}
        </>
      );
      break;
    case "anti_heal":
      body = (
        <>
          {withTarget(
            i18n._({
              ...HEALED,
              values: {
                healer: target?.name ?? "",
                amount: formatNumber(spec.amount ?? 0, i18n.locale),
              },
            }),
            null,
          )}
          {enemies(t`Enemies`)}
        </>
      );
      break;
    case "killer_counters": {
      const sources = spec.target_sources
        .map((source) =>
          i18n._({
            ...SOURCE_SHARE,
            values: {
              name: view(source).name,
              share: formatPercent(source.value ?? 0, i18n.locale),
            },
          }),
        )
        .join(" · ");
      body = withTarget(
        i18n._({
          ...KILLED_BY,
          values: { killer: target?.name ?? "", count: spec.count ?? 0, total: spec.total ?? 0 },
        }),
        sources === "" ? i18n._({ ...YOU_PLAY, values: { hero: own.name } }) : withHero(sources),
      );
      break;
    }
    case "finish_him":
      body = withTarget(
        i18n._({ ...ESCAPED, values: { enemy: target?.name ?? "", count: spec.count ?? 0 } }),
        spec.moment_s == null ? null : withHero(i18n._({ ...ESCAPED_FIRST, values: { time } })),
      );
      break;
    case "defense_item": {
      const kind = spec.damage_kind ?? "melee";
      body = (
        <>
          <div className="flex items-center gap-4">
            <span className="font-mono text-[28px] font-semibold text-accent">
              {formatPercent(spec.damage_share ?? 0, i18n.locale)}
            </span>
            <span className="flex flex-col gap-0.5">
              <span className="font-semibold">{i18n._({ ...DAMAGE_SHARE, values: { kind } })}</span>
              <span className="text-sm text-muted">
                {withHero(i18n._({ ...NO_DEFENSE, values: { time } }))}
              </span>
            </span>
          </div>
          {spec.enemy_hero_ids.length > 0 && enemies(t`Hit you the most`)}
        </>
      );
      break;
    }
    default:
      return null;
  }
  return (
    <section
      aria-label={t`Situation`}
      className="flex flex-col gap-3 rounded-xl border border-border bg-surface px-5 py-4"
    >
      {body}
    </section>
  );
};
